import React, { useState, useCallback } from 'react';
import api from 'api';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import axios from 'axios';
import { baseUrl } from '../config';
import Logo from '../assets/img/brand/veriscan-white.png';
import '../assets/css/auth.css';

const RULES = [
  { key: 'length', label: 'Minimum 8 characters', test: (pw) => pw.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lower', label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { key: 'number', label: 'One number', test: (pw) => /[0-9]/.test(pw) },
  { key: 'special', label: 'One special character (!@#$%^&*)', test: (pw) => /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?]/.test(pw) },
];

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(null);
  const mainContent = React.useRef(null);
  const history = useHistory();
  const params = useParams();
  const token = params?.token;

  React.useEffect(() => {
    axios
      .get(`${baseUrl}/auth/verify-token/${token}`)
      .then(() => setVerified(true))
      .catch(() => {
        toast.error('Your reset link has expired');
        history.push('/auth/login');
      });
  }, [token, history]);

  React.useEffect(() => {
    document.body.classList.add('bg-default');
    return () => document.body.classList.remove('bg-default');
  }, []);

  const ruleResults = RULES.map((r) => ({ ...r, pass: r.test(password) }));
  const allRulesPass = ruleResults.every((r) => r.pass);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRulesPass && passwordsMatch && !loading;

  const handleSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    if (!allRulesPass) {
      toast.error('Please meet all password requirements');
      return;
    }
    setLoading(true);
    api('put', `/auth/reset-password/${token}`, { password })
      .then(() => {
        toast.success('Password reset successfully!');
        setTimeout(() => history.push('/auth/login'), 1500);
      })
      .catch(() => {
        setLoading(false);
        toast.error('Failed to reset password. Please try again.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, passwordsMatch, allRulesPass, token, history]);

  if (verified === null) return null;

  return (
    <div className="auth-page" ref={mainContent}>
      <nav className="auth-navbar">
        <a href="https://getveriscan.com" className="auth-navbar__logo">
          <img src={Logo} alt="VeriScan" />
        </a>
      </nav>

      <div className="auth-content">
        <div className="auth-card">
          <div className="auth-card__header">
            <div className="auth-card__logo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
              </svg>
            </div>
            <h1 className="auth-card__title">Set new password</h1>
            <p className="auth-card__subtitle">
              Choose a strong password to protect your account
            </p>
          </div>

          <div className="auth-card__body">
            <form onSubmit={handleSubmit} noValidate>
              <div className="auth-field">
                <label className="auth-label" htmlFor="new-password">
                  New password
                </label>
                <div className="auth-input-wrap">
                  <i className="ni ni-lock-circle-open auth-input-wrap__icon" />
                  <input
                    id="new-password"
                    className="auth-input"
                    type={showPassword ? 'text' : 'password'}
                    name="new-password"
                    autoComplete="new-password"
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ paddingRight: 44 }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="auth-input-wrap__toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'} />
                  </button>
                </div>
              </div>

              <div className="auth-field">
                <label className="auth-label" htmlFor="confirm-password">
                  Confirm password
                </label>
                <div className="auth-input-wrap">
                  <i className="ni ni-lock-circle-open auth-input-wrap__icon" />
                  <input
                    id="confirm-password"
                    className="auth-input"
                    type={showConfirm ? 'text' : 'password'}
                    name="confirm-password"
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    className="auth-input-wrap__toggle"
                    onClick={() => setShowConfirm(!showConfirm)}
                    tabIndex={-1}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    <i className={showConfirm ? 'fa fa-eye-slash' : 'fa fa-eye'} />
                  </button>
                </div>
              </div>

              {password.length > 0 && (
                <div className="auth-pw-rules">
                  <p className="auth-pw-rules__title">Password requirements</p>
                  <div className={`auth-pw-rule ${passwordsMatch ? 'auth-pw-rule--pass' : 'auth-pw-rule--fail'}`}>
                    <span className="auth-pw-rule__icon">
                      <i className={passwordsMatch ? 'fa fa-check' : 'fa fa-minus'} />
                    </span>
                    <p className="auth-pw-rule__text">Passwords match</p>
                  </div>
                  {ruleResults.map((r) => (
                    <div key={r.key} className={`auth-pw-rule ${r.pass ? 'auth-pw-rule--pass' : 'auth-pw-rule--fail'}`}>
                      <span className="auth-pw-rule__icon">
                        <i className={r.pass ? 'fa fa-check' : 'fa fa-minus'} />
                      </span>
                      <p className="auth-pw-rule__text">{r.label}</p>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="submit"
                className="auth-btn auth-btn--primary"
                disabled={!canSubmit}
              >
                {loading ? <span className="auth-btn__spinner" /> : 'Save password'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                type="button"
                className="auth-forgot"
                onClick={() => history.push('/auth/login')}
                style={{ fontSize: 13 }}
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
